import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { trackEvent } from '../lib/consent';

export default function LanguageSelector({ className = '' }) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ||
    SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) ||
    SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setIsOpen(false);
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (code) => {
    i18n.changeLanguage(code);
    trackEvent('language_change', { language: code });
    setIsOpen(false);
  };

  return (
    <div className={`lang-selector ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className="lang-selector__btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`${t('nav.selectLanguage', 'Language')}: ${currentLang.short}`}
        title={currentLang.native}
      >
        <Icon name="language" size={17} className="lang-selector__globe" />
        <span className="lang-selector__code">{currentLang.short}</span>
        <Icon name="chevronDown" size={12} className="lang-selector__arrow" />
      </button>

      {isOpen && (
        <div className="lang-selector__menu" role="listbox" aria-label={t('nav.selectLanguage', 'Language')}>
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isSelected = currentLang.code === lang.code;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`lang-selector__item${isSelected ? ' is-selected' : ''}`}
                onClick={() => handleSelect(lang.code)}
              >
                <span className="lang-selector__item-native">{lang.native}</span>
                {isSelected && <Icon name="check" size={15} className="lang-selector__check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
